/* Snipping the individual drawings out of a presentation board.
 *
 * A board is a set of drawings laid out inside ruled rectangles. Asking a vision
 * model for pixel coordinates is unreliable; finding the rules is arithmetic. So
 * geometry proposes the rectangles, we number them on a copy of the board, and
 * the model only has to say what each numbered rectangle IS and which are worth
 * presenting. Coordinates from pixels, meaning from the model. */

export interface Cell {
  n: number;
  /** Normalised to the page: 0–1, so it survives any render resolution. */
  x: number; y: number; w: number; h: number;
  /** Fraction of the cell covered in ink — near-zero means an empty box. */
  ink: number;
}

const INK_LEVEL = 200;   // luminance below this is ink
const RULE_COVER = 0.45; // a row/column this inked is a drawn rule

async function toCanvas(dataUri: string): Promise<HTMLCanvasElement> {
  const img = await new Promise<HTMLImageElement>((res, rej) => {
    const i = new Image();
    i.onload = () => res(i); i.onerror = rej; i.src = dataUri;
  });
  const c = document.createElement("canvas");
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  const ctx = c.getContext("2d", { alpha: false })!;
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(img, 0, 0);
  return c;
}

function inkMask(ctx: CanvasRenderingContext2D, w: number, h: number): Uint8Array {
  const { data } = ctx.getImageData(0, 0, w, h);
  const m = new Uint8Array(w * h);
  for (let i = 0, p = 0; p < m.length; i += 4, p++) {
    const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    m[p] = lum < INK_LEVEL ? 1 : 0;
  }
  return m;
}

/** Positions of the ruled lines running most of the way across the sheet. */
function ruleLines(mask: Uint8Array, w: number, h: number, axis: "x" | "y"): number[] {
  const n = axis === "x" ? w : h;
  const span = axis === "x" ? h : w;
  const hits: number[] = [];
  for (let i = 0; i < n; i++) {
    let c = 0;
    for (let j = 0; j < span; j++) {
      const x = axis === "x" ? i : j;
      const y = axis === "x" ? j : i;
      if (mask[y * w + x]) c++;
    }
    if (c / span >= RULE_COVER) hits.push(i);
  }
  const out: number[][] = [];
  for (const i of hits) {
    if (out.length && i - out[out.length - 1][1] <= 3) out[out.length - 1][1] = i;
    else out.push([i, i]);
  }
  return out.map(([a, b]) => Math.round((a + b) / 2));
}

function density(mask: Uint8Array, w: number, r: number[]): number {
  const [x0, y0, x1, y1] = r;
  let c = 0, total = 0;
  for (let y = y0 + 3; y < y1 - 3; y += 2)
    for (let x = x0 + 3; x < x1 - 3; x += 2) { total++; if (mask[y * w + x]) c++; }
  return total ? c / total : 0;
}

/** The rectangles a board is divided into, largest first. */
export async function detectCells(dataUri: string, opts: { minFrac?: number } = {}): Promise<Cell[]> {
  const minFrac = opts.minFrac ?? 0.05;
  const canvas = await toCanvas(dataUri);
  const W = canvas.width, H = canvas.height;
  const mask = inkMask(canvas.getContext("2d")!, W, H);

  const dedupe = (list: number[], edge: number) =>
    [0, ...list, edge - 1].filter((v, i, a) => i === 0 || v - a[i - 1] > 8);
  const vs = dedupe(ruleLines(mask, W, H, "x"), W);
  const hs = dedupe(ruleLines(mask, W, H, "y"), H);

  const cells: Cell[] = [];
  for (let r = 0; r < hs.length - 1; r++)
    for (let c = 0; c < vs.length - 1; c++) {
      const rect = [vs[c], hs[r], vs[c + 1], hs[r + 1]];
      const cw = rect[2] - rect[0], ch = rect[3] - rect[1];
      if (cw < W * minFrac || ch < H * minFrac) continue;
      const ink = density(mask, W, rect);
      if (ink < 0.0006) continue;                       // an empty box
      cells.push({ n: 0, x: rect[0] / W, y: rect[1] / H, w: cw / W, h: ch / H, ink });
    }

  /* A drawing's title and scale bar sit under it, behind a rule that spans the
     column — so they read as a separate cell. Absorb a short strip into the cell
     directly above it in the same column. Strictly once each, and never past
     half the sheet, or the merge cascades and swallows the board. */
  const STRIP = 0.22, CEILING = 0.55;
  const absorbed = new Set<Cell>();   // strips folded into the drawing above
  const grown = new Set<Cell>();      // cells that have already taken one strip
  for (const strip of [...cells].sort((a, b) => a.y - b.y)) {
    if (absorbed.has(strip) || grown.has(strip) || strip.h > STRIP) continue;
    const above = cells.find((c) =>
      c !== strip && !absorbed.has(c) && !grown.has(c) &&
      Math.abs(c.x - strip.x) < 0.01 && Math.abs(c.x + c.w - strip.x - strip.w) < 0.01 &&
      Math.abs(c.y + c.h - strip.y) < 0.05 && c.h + strip.h <= CEILING);
    if (!above) continue;
    above.h = strip.y + strip.h - above.y;
    absorbed.add(strip);
    grown.add(above);
  }
  /* Keep the drawings: not an absorbed strip, and wide enough to be a drawing
     rather than the title-block column down the edge of the sheet. */
  const kept = cells.filter((c) => !absorbed.has(c) && c.w >= 0.1 && c.h >= 0.08);

  /* Reading order — top to bottom, left to right — so the numbers the model
     sees match the way a person scans the sheet. */
  kept.sort((a, b) => (Math.abs(a.y - b.y) > 0.05 ? a.y - b.y : a.x - b.x));
  kept.forEach((c, i) => { c.n = i + 1; });
  cells.length = 0;
  cells.push(...kept);
  /* Whole-sheet fallback: if the rules gave us nothing useful, the board is one
     drawing, and saying so beats snipping it into nonsense. */
  if (!cells.length) return [{ n: 1, x: 0, y: 0, w: 1, h: 1, ink: 1 }];
  return cells;
}

/** A copy of the board with each rectangle outlined and numbered, for the model
 *  to read. It never has to invent coordinates — only name what it sees. */
export async function annotateCells(dataUri: string, cells: Cell[], width = 1500): Promise<string> {
  const src = await toCanvas(dataUri);
  const scale = width / src.width;
  const c = document.createElement("canvas");
  c.width = Math.round(src.width * scale);
  c.height = Math.round(src.height * scale);
  const ctx = c.getContext("2d", { alpha: false })!;
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(src, 0, 0, c.width, c.height);

  const tag = Math.max(22, Math.round(c.width * 0.028));
  ctx.lineWidth = Math.max(2, Math.round(c.width * 0.0022));
  ctx.font = `700 ${Math.round(tag * 0.62)}px system-ui, sans-serif`;
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  for (const cell of cells) {
    const x = cell.x * c.width, y = cell.y * c.height, w = cell.w * c.width, h = cell.h * c.height;
    ctx.strokeStyle = "#E5484D";
    ctx.strokeRect(x + 1, y + 1, w - 2, h - 2);
    ctx.fillStyle = "#E5484D";
    ctx.fillRect(x + 3, y + 3, tag, tag);
    ctx.fillStyle = "#fff";
    ctx.fillText(String(cell.n), x + 3 + tag / 2, y + 3 + tag / 2 + 1);
  }
  return c.toDataURL("image/jpeg", 0.85);
}

/** Cut one rectangle out of the board at presentation resolution. A little bleed
 *  keeps the viewport's own frame line out of the crop. */
export async function cropCell(dataUri: string, cell: Cell, targetW = 1500, inset = 0.004, quality = 0.86): Promise<string> {
  const src = await toCanvas(dataUri);
  const x = Math.max(0, Math.round((cell.x + inset) * src.width));
  const y = Math.max(0, Math.round((cell.y + inset) * src.height));
  const w = Math.max(8, Math.round((cell.w - inset * 2) * src.width));
  const h = Math.max(8, Math.round((cell.h - inset * 2) * src.height));
  const scale = Math.min(1, targetW / w);
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(w * scale));
  c.height = Math.max(1, Math.round(h * scale));
  const ctx = c.getContext("2d", { alpha: false })!;
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(src, x, y, w, h, 0, 0, c.width, c.height);
  return c.toDataURL("image/jpeg", quality);
}
